import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';
import FacebookFeed from '../components/FacebookFeed';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const data = await getPageBySlug('index');
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Vissenbjerg Bageri · Friskbagt brød, brunsviger og kage',
    description: pageData?.meta_description || 'Vissenbjerg Bageri tilbyder friskbagt surdejsbrød, langtidshævet stenovnsbrød, lækre kager, fynsk brunsviger og smukke konditorkager. Besøg os i Vissenbjerg.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate">
      {/* Hero Banner */}
      <section className="relative h-[80vh] min-h-[500px] w-full overflow-hidden bg-transparent text-cream">
        {/* Baggrundsbillede med langsom zoom */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            src="https://assets.auxodev.dk/assets/0298fb46-c259-4129-b655-7c68adb8d499/shutterstock-1104806249.mov"
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover object-bottom scale-135 sm:scale-150 origin-bottom"
          />
        </div>

        {/* Hero Indhold */}
        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col items-center justify-center px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-col items-center justify-center space-y-6 max-w-2xl">
            <img
              src="https://assets.auxodev.dk/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/logo-negativ.svg"
              alt="Vissenbjerg Bageri"
              className="h-48 sm:h-64 md:h-80 w-auto transition-transform duration-500 hover:scale-105 filter drop-shadow-[0_10px_15px_rgba(0,0,0,0.5)]"
            />
          </div>
        </div>
      </section>

      {/* Kontaktbokse (De 4 kasser) */}
      <section className="relative z-20 mx-auto -mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Telefon-kasse */}
          <a
            href="tel:64471266"
            className="flex flex-col items-center justify-center rounded-xl bg-white p-6 text-center shadow-xl border border-gold-brown/10 hover:border-gold-brown/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-beige text-gold-brown group-hover:bg-gold-brown group-hover:text-white transition-colors duration-300">
              <i className="fa-solid fa-phone text-xl" />
            </div>
            <span className="text-xs uppercase tracking-wider text-chocolate/50 font-semibold">Ring til os</span>
            <span className="mt-1 font-serif text-lg font-bold text-chocolate group-hover:text-gold-brown transition-colors whitespace-nowrap">
              64 47 12 66
            </span>
          </a>

          {/* Mail-kasse */}
          <a
            href="mailto:info@vissenbjergbageri.dk"
            className="flex flex-col items-center justify-center rounded-xl bg-white p-6 text-center shadow-xl border border-gold-brown/10 hover:border-gold-brown/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-beige text-gold-brown group-hover:bg-gold-brown group-hover:text-white transition-colors duration-300">
              <i className="fa-solid fa-envelope text-xl" />
            </div>
            <span className="text-xs uppercase tracking-wider text-chocolate/50 font-semibold">Skriv en mail</span>
            <span className="mt-1 font-serif text-lg font-bold text-chocolate group-hover:text-gold-brown transition-colors whitespace-nowrap">
              info@vissenbjergbageri.dk
            </span>
          </a>

          {/* Facebook-kasse */}
          <a
            href="https://www.facebook.com/vissenbjergbageri.dk/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center rounded-xl bg-white p-6 text-center shadow-xl border border-gold-brown/10 hover:border-gold-brown/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-beige text-gold-brown group-hover:bg-gold-brown group-hover:text-white transition-colors duration-300">
              <i className="fa-brands fa-facebook-f text-xl" />
            </div>
            <span className="text-xs uppercase tracking-wider text-chocolate/50 font-semibold">Følg os</span>
            <span className="mt-1 font-serif text-lg font-bold text-chocolate group-hover:text-gold-brown transition-colors">
              Facebook
            </span>
          </a>

          {/* Instagram-kasse */}
          <a
            href="https://www.instagram.com/vissenbjergbageri"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center rounded-xl bg-white p-6 text-center shadow-xl border border-gold-brown/10 hover:border-gold-brown/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-beige text-gold-brown group-hover:bg-gold-brown group-hover:text-white transition-colors duration-300">
              <i className="fa-brands fa-instagram text-xl" />
            </div>
            <span className="text-xs uppercase tracking-wider text-chocolate/50 font-semibold">Se billeder</span>
            <span className="mt-1 font-serif text-lg font-bold text-chocolate group-hover:text-gold-brown transition-colors">
              Instagram
            </span>
          </a>
        </div>
      </section>

      {/* Kombineret Stolthed & Håndværk Sektion */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Venstre side: Visuel komposition med brød-billede og diplom */}
          <div className="lg:col-span-5 relative flex justify-center">
            {/* Hovedbillede fra scrape (Brød) */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gold-brown/10 aspect-[4/5] w-full max-w-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/img-9315.jpg"
                alt="Friskbagt surdejsbrød og stenovnsbrød hos Vissenbjerg Bageri"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-chocolate/40 via-transparent to-transparent" />
            </div>

            {/* Overlappende Diplom-kort */}
            <div className="absolute -bottom-6 right-4 sm:right-12 md:right-16 lg:-right-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-gold-brown/20 max-w-[220px] flex flex-col items-center text-center space-y-2">
              <img
                src="https://assets.auxodev.dk/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/diplom.svg"
                alt="Fyns bedste bageri diplom"
                className="w-24 h-auto drop-shadow-md"
              />
              <div>
                <h4 className="font-serif text-sm font-bold text-chocolate">Fyns Bedste Bageri</h4>
                <p className="text-[11px] text-gold-brown font-semibold uppercase tracking-wider">Kåret i 2020</p>
              </div>
            </div>
          </div>

          {/* Højre side: Tekst, citat og information */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-3">
              <span className="text-xs uppercase tracking-widest text-gold-brown font-bold flex items-center gap-2">
                <span className="h-px w-8 bg-gold-brown" />
                Vores Stolthed & Håndværk
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-chocolate leading-tight">
                I LAVER DE LÆKRESTE BRØD OG KAGER!
              </h2>
              <p className="font-accent text-3xl md:text-4xl text-gold-brown leading-none pt-1">
                – En sætning vi ofte hører her i butikken
              </p>
            </div>

            <div className="relative pl-6 border-l-2 border-gold-brown/30 space-y-2">
              <p className="text-base text-chocolate/80 italic leading-relaxed">
                Det er vi naturligvis glade for og meget stolte af. En sådan en tilkendegivelse is stærkt motiverende!
              </p>
            </div>

            <div className="space-y-4 text-base text-chocolate/80 leading-relaxed">
              <p>
                Vi tilbyder friskbagt surdejsbrød og langtidshævet stenovnsbrød gennem hele dagen og har et bredt udvalg af wienerbrød, småkager og flødekager til kaffen, ligesom vi også står klar til at kreere din bryllupskage, barnedåbskage eller specielle fødselsdagslagkage. Ud over det tilbyder vi også sandwichs og to go produkter.
              </p>
              <p>
                Vi er rigtig glade og stolte af at blive kåret til <strong className="text-chocolate">Fyns bedste bageri samt nr. 2 i Danmark</strong>. Dette lader sig kun gøre ved, at alle vores medarbejder i butikken og bageriet hver dag yder en indsats ud over det sædvanlige. Samt at vi har et stort og loyalt kunde ophav. <span className="font-serif font-bold text-gold-brown">Tusind tak!</span>
              </p>
            </div>

            {/* CTA Knapper */}
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="https://goo.gl/maps/wFjyWbJ8J69dYnSA9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-chocolate px-6 py-3.5 text-sm font-semibold text-cream hover:bg-gold-brown hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                <i className="fa-solid fa-map-location-dot" />
                Besøg os på Vestergade 5
              </a>
              <Link
                to="/bestilling"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-semibold text-chocolate border border-chocolate/20 hover:border-chocolate hover:bg-beige/10 hover:-translate-y-0.5 transition-all duration-200"
              >
                <i className="fa-solid fa-bread-slice" />
                Bestil brød & kager
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Interaktiv Facebook Feed Sektion */}
      <FacebookFeed />

      {/* De tre kategorier (Brød, Kager, Brunsviger) */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {/* Brød */}
          <div className="rounded-3xl bg-white/80 backdrop-blur-md overflow-hidden shadow-xl border border-gold-brown/10 flex flex-col h-full hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300">
            <div className="h-64 overflow-hidden relative group">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/pp-celtic-2000x2000.jpg"
                alt="Brød"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4 bg-chocolate/90 backdrop-blur-sm text-cream text-xs font-bold px-3 py-1 rounded-full border border-gold-brown/20">
                Friskbagt dagligt
              </div>
            </div>
            <div className="p-6 md:p-8 flex flex-col flex-grow space-y-4">
              <div className="space-y-1">
                <span className="font-accent text-2xl text-gold-brown block leading-none">Friskbagt håndværk</span>
                <h3 className="font-serif text-2xl font-bold text-chocolate">Brød</h3>
              </div>
              <p className="text-sm text-chocolate/70 leading-relaxed flex-grow">
                Friskbagt surdejsbrød, langtidshævet stenovnsbrød, rundstykker og morgenbrød bagt helt fra bunden hver dag.
              </p>
              <div className="pt-2">
                <Link
                  to="/broed"
                  className="inline-flex items-center gap-2 text-sm font-bold text-gold-brown hover:text-copper transition-colors group"
                >
                  Læs mere her <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          {/* Kager */}
          <div className="rounded-3xl bg-white/80 backdrop-blur-md overflow-hidden shadow-xl border border-gold-brown/10 flex flex-col h-full hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300">
            <div className="h-64 overflow-hidden relative group">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/b8-1.jpg"
                alt="Kager"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4 bg-chocolate/90 backdrop-blur-sm text-cream text-xs font-bold px-3 py-1 rounded-full border border-gold-brown/20">
                Konditorkunst
              </div>
            </div>
            <div className="p-6 md:p-8 flex flex-col flex-grow space-y-4">
              <div className="space-y-1">
                <span className="font-accent text-2xl text-gold-brown block leading-none">Søde fristelser</span>
                <h3 className="font-serif text-2xl font-bold text-chocolate">Kager</h3>
              </div>
              <p className="text-sm text-chocolate/70 leading-relaxed flex-grow">
                Smukke konditorkager, flødekager, wienerbrød og festlagkager til enhver anledning.
              </p>
              <div className="pt-2">
                <Link
                  to="/kager"
                  className="inline-flex items-center gap-2 text-sm font-bold text-gold-brown hover:text-copper transition-colors group"
                >
                  Læs mere her <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          {/* Brunsviger */}
          <div className="rounded-3xl bg-white/80 backdrop-blur-md overflow-hidden shadow-xl border border-gold-brown/10 flex flex-col md:col-span-2 lg:col-span-1 md:flex-row lg:flex-col h-full hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300">
            <div className="h-64 md:h-auto md:w-1/2 lg:w-full lg:h-64 overflow-hidden relative shrink-0 group">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/brunsviger-kagemand-kagedame.jpg"
                alt="Brunsviger"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4 bg-chocolate/90 backdrop-blur-sm text-cream text-xs font-bold px-3 py-1 rounded-full border border-gold-brown/20">
                Fynsk specialitet
              </div>
            </div>
            <div className="p-6 md:p-8 lg:p-6 flex flex-col flex-grow justify-between space-y-4">
              <div className="space-y-4 flex-grow">
                <div className="space-y-1">
                  <span className="font-accent text-2xl text-gold-brown block leading-none">Fyns nationalspise</span>
                  <h3 className="font-serif text-2xl font-bold text-chocolate">Brunsviger</h3>
                </div>
                <p className="text-sm text-chocolate/70 leading-relaxed">
                  Den helt rigtige fynske brunsviger. Vi laver kagemænd, kagedamer og bryllupsbrunsvigere.
                </p>
              </div>
              <div className="pt-2 flex flex-col gap-2">
                <Link
                  to="/brunsviger"
                  className="inline-flex items-center gap-2 text-sm font-bold text-gold-brown hover:text-copper transition-colors group"
                >
                  Læs mere her <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="https://brunsvigermand.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-chocolate/70 hover:text-gold-brown transition-colors"
                >
                  <span>Bestil kagemand på brunsvigermand.dk</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dygtige og kreative medarbejdere */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <div className="bg-white/85 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-gold-brown/10 space-y-6 relative overflow-hidden">
            <div className="absolute -top-6 -right-6 text-gold-brown/5 text-9xl font-serif pointer-events-none select-none">
              B
            </div>
            <div className="space-y-2">
              <span className="font-accent text-4xl text-gold-brown block leading-none">Det stærke bagerteam</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-chocolate tracking-tight">Dygtige og kreative medarbejdere</h2>
            </div>
            <div className="w-16 h-1 bg-gold-brown/30 rounded-full" />
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vi sætter en stor ære i at have kvalificerede og dygtige medarbejdere, der alle værner om de gamle håndværkstraditioner samtidig med, at de er innovative og kreative.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              De er hver dag med til at bevare vores gode forretning, og uden dem kunne vi ikke udvikle og forbedre vores opskrifter og bagværk og i sidste ende skabe gode smagsoplevelser for vores kunder.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Udover bagermester selv har vi i øjeblikket fire bagersvende, en lærling og en konditor ansat i forretningen.
            </p>
            <div className="pt-4 border-t border-gold-brown/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-beige flex items-center justify-center text-gold-brown shrink-0">
                <i className="fa-solid fa-users text-xl" />
              </div>
              <div>
                <p className="text-sm font-bold text-chocolate">Vores dedikerede team</p>
                <p className="text-xs text-chocolate/60">4 bagersvende, 1 lærling, 1 konditor & bagermesteren</p>
              </div>
            </div>
          </div>
        </div>
        <div className="relative">
          {/* Decorative background shape */}
          <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-gold-brown/10 to-transparent -rotate-2 scale-105 pointer-events-none" />
          
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white h-[450px] group">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/img-9466.jpg"
              alt="Bageriets medarbejdere"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />
            {/* Soft overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-chocolate/40 via-transparent to-transparent opacity-60" />
            
            {/* Floating badge */}
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-gold-brown/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-gold-brown font-bold">Håndværk & Passion</p>
                <p className="text-sm font-serif font-bold text-chocolate">Bagværk lavet fra bunden</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gold-brown text-white flex items-center justify-center shrink-0">
                <i className="fa-solid fa-heart" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Din foretrukne bagermester */}
      <section className="bg-chocolate text-cream py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gold-brown/20 h-96 order-last md:order-first">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/59682034-1599745960169796-3347904372754350080-n.jpg"
              alt="Bageriet i Vestergade siden 1925"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-6">
            <h2 className="font-serif text-3xl font-bold text-cream">Din foretrukne bagermester</h2>
            <h3 className="font-serif text-xl text-gold-brown italic">Vores bageri har ligget i Vestergade siden 1925.</h3>
            <p className="text-base text-cream/80 leading-relaxed">
              Hver dag stræber vi efter at kunne levere endnu bedre varer og smagsoplevelser for vores kunder, for derigennem at stadfæste os som din foretrukne bagermester – uanset om der er tale om klassisk brødfremstilling, udvikling af nye langtidshævede surdejs- og stenovnsbrød eller detaljerede og æstetiske lagkagekreationer.
            </p>
            <p className="text-base text-cream/80 leading-relaxed">
              Her på hjemmesiden kan du læse meget mere om vores bagværk, og du er også velkommen til at kigge ind forbi vores nyrenoverede bagerforretning, hvis du ønsker at vide mere.
            </p>
          </div>
        </div>
      </section>

      {/* Hvad er en lækker kage og et godt brød? */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 relative" data-dev-id="src/pages/Home.jsx:375:6">
        {/* Subtle background wheat icon */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
          <i className="fa-solid fa-wheat-awn text-[18rem] md:text-[24rem] text-gold-brown"></i>
        </div>
        
        <div className="relative bg-beige/30 backdrop-blur-md border border-gold-brown/15 rounded-3xl p-8 md:p-12 shadow-lg max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left Column: Text Content */}
          <div className="lg:col-span-7 space-y-8 text-left relative z-10">
            {/* Decorative Top Accent */}
            <div className="flex flex-col items-start space-y-2">
              <span className="font-accent text-3xl md:text-4xl text-gold-brown">Vores Bagerfilosofi</span>
              <div className="flex items-center gap-3 w-full max-w-xs">
                <i className="fa-solid fa-wheat-awn text-gold-brown text-lg"></i>
                <span className="h-[1px] bg-gold-brown/30 flex-1"></span>
              </div>
            </div>

            {/* First Quote Block */}
            <div className="space-y-4">
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate tracking-wide uppercase">
                Hvad er en lækker kage og et godt brød?
              </h2>
              <div className="relative pl-6 border-l-2 border-gold-brown/30">
                <p className="font-serif text-lg md:text-xl text-chocolate/90 italic leading-relaxed">
                  Vi mener det bedste resultat skabes i et kreativt bageri, hvor personalet hver dag inspirerer hinanden til nye spændende brød og kagetyper – selvfølgelig lavet helt fra bunden!
                </p>
              </div>
            </div>

            {/* Elegant Divider */}
            <div className="flex items-center gap-2 py-1">
              <span className="w-2 h-2 rounded-full bg-gold-brown/40"></span>
              <span className="w-16 h-[1px] bg-gold-brown/30"></span>
              <span className="w-2 h-2 rounded-full bg-gold-brown/40"></span>
            </div>

            {/* Second Quote Block */}
            <div className="space-y-3">
              <h3 className="font-serif text-xl md:text-2xl font-bold text-chocolate tracking-wide uppercase">
                Hvis vi selv skal sige det …
              </h3>
              <p className="font-accent text-3xl md:text-4xl text-gold-brown leading-relaxed">
                … Så synes vi faktisk at vi gør det godt!
              </p>
              <p className="font-serif text-base md:text-lg text-chocolate/80 italic">
                Og det kan smages i vores bagværk og vores kager hver eneste dag.
              </p>
            </div>
          </div>

          {/* Right Column: Image Presentation */}
          <div className="lg:col-span-5 relative z-10">
            <div className="relative group">
              {/* Decorative background frame */}
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-tr from-gold-brown/20 to-transparent opacity-70 group-hover:scale-105 transition-transform duration-500 -rotate-2"></div>
              
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden border border-gold-brown/10 shadow-xl">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/brunsviger-bryllupskage.jpg" 
                  alt="Smuk bryllupskage og fynsk brunsviger" 
                  className="w-full h-[350px] md:h-[450px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-chocolate/60 via-transparent to-transparent"></div>
                
                {/* Floating Badge */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded-xl border border-gold-brown/15 shadow-lg text-center">
                  <span className="block font-accent text-2xl text-gold-brown">Ægte Fynsk Kagekunst</span>
                  <span className="block text-xs uppercase tracking-widest text-chocolate/70 font-serif mt-1">Håndlavet fra bunden</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Decorative Corner Borders */}
          <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-gold-brown/20 rounded-tl-lg pointer-events-none"></div>
          <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gold-brown/20 rounded-tr-lg pointer-events-none"></div>
          <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gold-brown/20 rounded-bl-lg pointer-events-none"></div>
          <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-gold-brown/20 rounded-br-lg pointer-events-none"></div>
        </div>
      </section>
    </div>
  );
}
